import React, { useState, useRef, useEffect } from 'react';

const TeamDisplay = ({ player, gameSyncData, role, onKickPlayer, onTransferHost }) => {
  const [openPlayerMenu, setOpenPlayerMenu] = useState(null);
  const menuRefs = useRef({});
  const playerId = player.id;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideSomeMenu = Object.values(menuRefs.current).some(
        (ref) => ref && ref.contains(event.target)
      );
      if (!clickedInsideSomeMenu) {
        setOpenPlayerMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!gameSyncData || !gameSyncData.teams || !gameSyncData.players) {
    return (
      <div className="bg-card-color backdrop-blur-md border border-indigo-700 rounded-xl p-4 w-full lg:w-80 shadow-md min-h-[200px] max-h-[60vh] overflow-y-auto lg:max-h-none lg:overflow-y-visible">
        <h2 className="text-xl font-bold text-indigo-300 mb-3">Teams</h2>
        <p className="text-gray-400">No players available</p>
      </div>
    );
  }

  const { teams, teamScores, players } = gameSyncData;
  const getPlayerDetails = (id) => players.find(p => p.id === id && p.online);
  const toggleMenu = (playerId) => {
    setOpenPlayerMenu(prev => (prev === playerId ? null : playerId));
  };


const Tooltip = ({ children, text }) => (
  <div className="relative group inline-block">
    {children}
    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
      {text}
    </div>
  </div>
);

const changeStats = (stats) => {
  const statusMap = {
    'host-waiting-other-team': (
      <>
        <Tooltip text="Host">⭐</Tooltip>
        <Tooltip text="Waiting">⏳</Tooltip>
      </>
    ),
    'waiting-other-team': (
      <Tooltip text="Waiting">⏳</Tooltip>
    ),
    'host-acting': (
      <>
        <Tooltip text="Host">⭐</Tooltip>
        <Tooltip text="Actor">🎤</Tooltip>
      </>
    ),
    'host-guessing': (
      <>
        <Tooltip text="Host">⭐</Tooltip>
        <Tooltip text="Guesser">👁️</Tooltip>
      </>
    ),
    'acting': (
      <Tooltip text="Actor">🎤</Tooltip>
    ),
    'guessing': (
      <Tooltip text="Guesser">👁️</Tooltip>
    ),
    'waiting': (
      <Tooltip text="Waiting">⏳</Tooltip>
    ),
  };

  return statusMap[stats] || <Tooltip text="Waiting">⏳</Tooltip>;
};

  return (
    <div className="bg-white rounded-bl-[2.5rem] p-6 w-full lg:w-80 shadow-md h-full overflow-y-auto">
      <h2 className="text-2xl font-bold mb-2" style={{ color: '#282828' }}>
        Teams
      </h2>
      <hr className="border-t-2 border-gray-300 mb-4" />
      {Object.entries(teams).map(([teamName, teamMembers], index) => (
        <div key={index} className="mb-6">
          <h3 className="font-semibold text-lg mb-2" style={{ color: '#282828' }}>
            {teamName}{' '}
            <span className="text-sm font-normal text-gray-500 notranslate" translate="no">
              ({teamScores?.[teamName] || 0} pts)
            </span>
          </h3>
          <ul className="ml-2 space-y-2">
            {teamMembers
              .map(getPlayerDetails)
              .filter(Boolean)
              .sort((a, b) => b.score - a.score)
              .map((player, i) => {
                const isPlayerHost = player.id === gameSyncData.hostId;
                const isCurrentUser = player.id === playerId;

                const rowBgColor = isCurrentUser
                  ? '#2A71F5'
                  : i % 2 === 0
                  ? '#F6EFEF'
                  : '#DBDBDB';

                return (
                  <li
                    key={player.id}
                    className={`relative text-sm px-4 py-2 rounded-full flex justify-between items-center transition-colors`}
                    style={{
                      backgroundColor: rowBgColor,
                      color: isCurrentUser ? '#FFFFFF' : '#282828',
                      cursor: role === 'host' && !isPlayerHost ? 'pointer' : 'default',
                    }}
                    onClick={() =>
                      role === 'host' && !isPlayerHost && toggleMenu(player.id)
                    }
                    ref={(el) => (menuRefs.current[player.id] = el)}
                  >
                    <span className="notranslate truncate max-w-[9rem]" translate="no" title={player.name}>
                      {player.name}
                    </span>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="flex items-center gap-1 cursor-default">{changeStats(player.status)}</span>
                      <span className="notranslate" translate="no">
                        {player.score} pts
                      </span>
                    </div>

                    {role === 'host' && openPlayerMenu === player.id && !isPlayerHost && (
                      <div className="absolute right-2 top-full mt-1 bg-indigo-800 text-white text-xs rounded shadow-lg py-1 z-10 w-36">
                        <button
                          className="block w-full text-left px-4 py-2 hover:bg-indigo-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTransferHost(player.id);
                            setOpenPlayerMenu(null);
                          }}
                        >
                          Transfer Host
                        </button>
                        <button
                          className="block w-full text-left px-4 py-2 hover:bg-red-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            onKickPlayer(player.id);
                            setOpenPlayerMenu(null);
                          }}
                        >
                          Kick Player
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            {teamMembers.length === 0 && (
              <li className="text-gray-500 italic">No players in this team</li>
            )}
          </ul>
        </div>
      ))}
    </div>

  );
};

export default TeamDisplay;
